import React from 'react';
import { ProfilePicture } from '../ProfilePicture';
import { EmployeePopoverCard } from './EmployeePopoverCard';
import { Employee } from '../../interfaces/interface';
import { formatStateName } from '../../utils/employeeUtils';
import { generatePhoneToolUrl, SLACK_DM_URL, SLACK_ICON_URL } from '../../../../../constants/AppConstants';
import { BADGE_COLOR_MAP } from '../../constants/EAHuddleConstants';
import './EmployeeListItem.scss';

interface EmployeeListItemProps {
  employee: Employee;
  onClick?: () => void;
  showDetails?: boolean;
  /** Show the `L{level}`/`TEMP` badge left of the profile picture. Defaults to true. */
  showLevel?: boolean;
  /**
   * Baseball-cards variant: hide the left-of-avatar level badge and instead render `L{level}`
   * inline as its own bullet segment immediately before the city in the details line
   * (job title • department • L{level} • city, state). Defaults to false so every existing
   * (Huddle+/admin/impersonation) caller keeps the left badge unchanged.
   */
  levelInlineBeforeCity?: boolean;
  disabled?: boolean;
  disableHover?: boolean;
  renderActions?: () => React.ReactNode;
  className?: string;
}

export const EmployeeListItem: React.FC<EmployeeListItemProps> = ({
  employee,
  onClick,
  showDetails = true,
  showLevel = true,
  levelInlineBeforeCity = false,
  disabled = false,
  disableHover = false,
  renderActions,
  className = ''
}) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const containerClassName = disabled
    ? 'employee-item hoverable-row--disabled'
    : onClick && !disableHover
    ? 'employee-item hoverable-row'
    : 'employee-item';
  const displayName = employee.displayName || employee.employee_name;

  const cityState = [employee.location_city_name, formatStateName(employee.location_state_name)].filter(Boolean).join(', ');

  // In the baseball-cards inline variant, level rides in the details line as its own bullet
  // segment right before the city (job title • department • L{level} • city, state). Omitted
  // when the level is absent — no `TEMP` placeholder inline, unlike the left badge.
  const inlineLevel = levelInlineBeforeCity && employee.job_level_name ? `L${employee.job_level_name}` : '';

  const detailParts = [employee.job_title_name, employee.department_name_current, inlineLevel, cityState].filter(Boolean);

  const detailsContent = detailParts.join(' • ');

  // Badge color applies to profile picture border, not level badge
  const badgeColors = employee.badgeColor ? BADGE_COLOR_MAP[employee.badgeColor] : undefined;
  const profileBorderColor = badgeColors?.background;

  // Level badge text: show TEMP if no level
  const levelText = employee.job_level_name ? `L${employee.job_level_name}` : 'TEMP';

  return (
    <div
      className={`${containerClassName} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-disabled={disabled}
    >
      <div className="employee-info-container">
        {showLevel && !levelInlineBeforeCity && <span className="level-badge">{levelText}</span>}
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <EmployeePopoverCard loginName={employee.login_name} renderWithPortal>
            <ProfilePicture loginName={employee.login_name} name={displayName} size="small" borderColor={profileBorderColor} />
          </EmployeePopoverCard>
        </span>
        <div className="employee-name-wrapper">
          <div className="employee-name-line">
            <a
              href={generatePhoneToolUrl(employee.login_name)}
              target="_blank"
              rel="noopener noreferrer"
              className="employee-name-link"
              onClick={(e) => e.stopPropagation()}
            >
              {displayName}
            </a>
            {employee.login_name && <span className="employee-login-text"> [{employee.login_name}]</span>}
            {employee.login_name && (
              <a
                href={`${SLACK_DM_URL}${employee.login_name}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`Slack ${employee.login_name}`}
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '4px' }}
              >
                <img src={SLACK_ICON_URL} alt="Slack" width="14" height="14" />
              </a>
            )}
          </div>
          {showDetails && detailsContent && (
            <div className="details-line" title={detailsContent}>
              <span className="details-text">{detailsContent}</span>
            </div>
          )}
        </div>
      </div>
      {renderActions && <div className="employee-item-actions">{renderActions()}</div>}
    </div>
  );
};
